import { Button } from 'antd'
import React from 'react'
import styled from 'styled-components/macro'
import FoodSafetyCorrectiveActionPanel from 'src/components/food-safety/FoodSafetyCorrectiveActionPanel'
import { FOOD_SAFETY_SECONDARY_TEXT } from 'src/constants/foodSafety'
import {
  canRecordFoodSafetyRecheck,
  foodSafetyRecheckWording,
  formatFoodSafetyCorrectiveActionBy,
  formatFoodSafetyExceptionHeadline,
  formatFoodSafetyLatestCorrectiveAction,
  formatFoodSafetyRecheck,
  formatFoodSafetyRecheckDue,
  isFoodSafetyExceptionOpen,
  isFoodSafetyRecheckOverdue,
} from 'src/utils/foodSafetyCorrectiveAction'

/**
 * Inline state of a saved reading's exception: "Needs corrective action",
 * or what was done, plus the recheck. `fallbackNote` covers old logs with
 * only a free-text note.
 */
export const FoodSafetyExceptionStatus = ({
  exception,
  fallbackNote = null,
  testId,
}) => {
  const actionText =
    formatFoodSafetyLatestCorrectiveAction(exception) || fallbackNote
  const actionBy = formatFoodSafetyCorrectiveActionBy(exception)
  const recheckText = formatFoodSafetyRecheck(exception?.recheck, {
    kind: exception?.kind,
    raisedAt: exception?.raisedAt,
  })
  const dueText = formatFoodSafetyRecheckDue(exception)
  return (
    <>
      {isFoodSafetyExceptionOpen(exception) ? (
        <NeedsAction data-testid={testId}>Needs corrective action</NeedsAction>
      ) : actionText ? (
        <Quiet data-testid={testId ? `${testId}-action` : undefined}>
          {actionText}
          {actionBy ? (
            <ActionBy data-testid={testId ? `${testId}-action-by` : undefined}>
              {' · '}
              {actionBy}
            </ActionBy>
          ) : null}
        </Quiet>
      ) : null}
      {recheckText ? (
        <Quiet data-testid={testId ? `${testId}-recheck` : undefined}>
          {recheckText}
        </Quiet>
      ) : null}
      {dueText ? (
        <RecheckDue
          data-overdue={
            isFoodSafetyRecheckOverdue(exception) ? 'true' : 'false'
          }
          data-testid={testId ? `${testId}-recheck-due` : undefined}
        >
          {dueText}
        </RecheckDue>
      ) : null}
    </>
  )
}

/**
 * "Record corrective action" / "Record recheck" links, opening the shared
 * panel inline. `followUps` is the screen's `useFoodSafetyExceptionFollowUps`.
 */
export const FoodSafetyExceptionFollowUp = ({
  exception,
  section,
  fallbackReading,
  viewingToday = false,
  followUps,
  testIdPrefix = 'fs',
  rowKey,
}) => {
  if (!exception?._id) return null
  const exceptionId = exception._id
  const open = isFoodSafetyExceptionOpen(exception)
  const canRecheck = canRecordFoodSafetyRecheck(exception, { viewingToday })
  const draft = followUps.drafts[exceptionId]
  const key = rowKey ?? exceptionId

  if (draft) {
    return (
      <FoodSafetyCorrectiveActionPanel
        section={section || exception.section}
        reading={exception.reading || fallbackReading}
        headline={formatFoodSafetyExceptionHeadline(exception, fallbackReading)}
        draft={draft}
        onChange={patch => followUps.update(exceptionId, patch)}
        showActions={open}
        showRecheck={canRecheck}
        saveLaterHint={false}
        autoFocus
        focusOnError
        error={followUps.errors[exceptionId]}
        testIdPrefix={`${testIdPrefix}-follow-up-${exceptionId}`}
      >
        <Actions>
          <Button
            type='primary'
            loading={followUps.savingId === exceptionId}
            onClick={() => followUps.save(exception, { viewingToday })}
            data-testid={`${testIdPrefix}-follow-up-save-${exceptionId}`}
          >
            Save
          </Button>
          <Button onClick={() => followUps.close(exceptionId)}>Cancel</Button>
        </Actions>
      </FoodSafetyCorrectiveActionPanel>
    )
  }

  if (!open && !canRecheck) return null
  return (
    <Links>
      {open ? (
        <Link
          type='button'
          onClick={() => followUps.open(exception)}
          data-testid={`${testIdPrefix}-record-action-${key}`}
          data-fs-exception-action={exceptionId}
        >
          Record corrective action
        </Link>
      ) : null}
      {canRecheck ? (
        <Link
          type='button'
          onClick={() => followUps.open(exception, { recheckOpen: true })}
          data-testid={`${testIdPrefix}-record-recheck-${key}`}
        >
          {foodSafetyRecheckWording(section || exception.section).followUpLabel}
        </Link>
      ) : null}
    </Links>
  )
}

const NeedsAction = styled.span`
  align-self: flex-start;
  font-size: 0.8rem;
  font-weight: 600;
  color: #8a4b00;
  background: #fff4e0;
  border-radius: 999px;
  padding: 0.1rem 0.6rem;
`

/** Card corner badge for a unit with an open exception. */
export const NeedsActionBadge = styled.span`
  flex-shrink: 0;
  align-self: flex-start;
  font-size: 0.8rem;
  font-weight: 600;
  color: #8a4b00;
  background: #fff4e0;
  border: 1px solid #f0d9b5;
  border-radius: 999px;
  padding: 0.1rem 0.6rem;
  white-space: nowrap;
`

const Quiet = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
`

const ActionBy = styled.span`
  white-space: nowrap;
`

export const RecheckDue = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};

  &[data-overdue='true'] {
    color: #a35b00;
  }
`

const Links = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.9rem;
`

const Link = styled.button`
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  text-decoration: underline;
`

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
`
